import { Button } from '@/components/button'
import { ArrowBack, CheckCircle, ChevronRight } from '@/components/icons'
import { PasswordInput } from '@/components/input'
import { eventTracker } from '@/config/trackingFunctions'
import { APP_CUSTOM_ERROR, APP_TIMEOUT_ERROR } from '@/constants/MessageConstants'
import { ROUTE_EXTERNAL_SIGNUP } from '@/constants/Routes'
import { useAppDispatch } from '@/hooks/useRedux'
import { getClientNameByKey } from '@/services'
import { SignupPayload } from '@/types'
import { createSignUpStep2ValidationSchema } from '@/utils/schema/SignUpStep2ValidationSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { signup } from '../auth/authSlice'
import ExternalScreen from './ExternalScreen'
import { ErrorMessage } from './components'
import { TRACKING_TYPES, PAGE_NAMES } from '@/constants/TrackingConstants'

const PASSWORD_INSTRUCTIONS = ['Must be at least 6 characters long', 'Must contain one or more numbers']

type FormValues = {
  password: string
  confirmPassword: string
}

export const ExternalCreatePassword = () => {
  const { t } = useTranslation()

  const { partnerId } = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const location = useLocation()

  const [errorMessage, setErrorMessage] = useState<string>('')
  const [successSignup, setSuccessSignup] = useState<boolean>(false)

  const validationSchema = createSignUpStep2ValidationSchema(t)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onTouched',
    resolver: yupResolver(validationSchema),
  })

  const goBack = () => {
    navigate(-1)
  }

  const onSubmit = handleSubmit(async (data) => {
    const accData = location.state?.accData
    if (!accData) {
      setErrorMessage('Account data is missing. Please start the signup process again.')
      return
    }

    const payload: SignupPayload = {
      fullName: accData.fullName?.trim() || '',
      birthdate: accData.birthdate,
      email: accData.email,
      gender: accData.gender,
      password: data.password,
      addressId: accData.addressId,
      phone: accData.phone,
      isoCode: accData.isoCode,
      marketingOptIn: accData.marketingOptIn,
      termsOfService: accData.termsOfService,
      clientKey: partnerId,
      ambassadorCode: '',
      mediaId: null,
      hand: 'NONE',
      defaultRating: 'DOUBLES',
      singlesRating: '',
      doublesRating: '',
      userId: accData.userId || null,
    }

    try {
      const resultAction = await dispatch(signup(payload)).unwrap()

      window.parent.postMessage(
        {
          userToken: resultAction.result.accessToken,
          refreshToken: resultAction.result.refreshToken,
          id: resultAction.result.user.id,
          duprId: resultAction.result.user.referralCode,
          stats: resultAction.result.user.stats,
        },
        '*'
)

      const partnerName = await getClientNameByKey(partnerId as string, resultAction.result.accessToken)

      eventTracker(TRACKING_TYPES.PARTNER_SIGNUP_SUCCESS_PASSWORD, {
        'Page Name': PAGE_NAMES.PARTNER_SIGNUP,
        partner_name: partnerName,
        partner_id: partnerId,
      })
      setSuccessSignup(true)
    } catch (err: any) {
      console.error('Error during signup or getting partner name:', err)
      if (err?.response?.data?.message) {
        const errorMessage = err.response.data.message

        setErrorMessage(errorMessage)
        eventTracker(TRACKING_TYPES.PARTNER_SIGNUP_ERROR, { errorMessage, 'Page Name': PAGE_NAMES.PARTNER_SIGNUP })
      } else if (err?.code === 'ECONNABORTED') {
        setErrorMessage(APP_TIMEOUT_ERROR)
      } else {
        setErrorMessage(APP_CUSTOM_ERROR)
      }
    }
  })

  useEffect(() => {
    if (!location.state) navigate(ROUTE_EXTERNAL_SIGNUP, { replace: true })
  }, [location, navigate])

  const renderSignupForm = () => (
    <>
      <div className="flex flex-row items-center justify-between w-full">
        <ArrowBack width={48} height={48} onClick={goBack} className="cursor-pointer" />
        <Button onClick={() => onSubmit()} className="text-primary-500 flex items-center">
          <p className="text-sm font-semibold">Create Account</p>
          <ChevronRight />
        </Button>
      </div>
      <div className="p-4">
        <p className="text-navy-900 font-bold text-[28px]">Create Password</p>
        {errorMessage && <ErrorMessage className="break-all" message={errorMessage} />}
      </div>
      <form className="px-6 flex flex-col gap-y-6">
        <PasswordInput
          instructs={PASSWORD_INSTRUCTIONS}
          label="Password"
          placeholder="Enter Password"
          labelClassName="!text-navy-900"
          iconClassName="!text-navy-900"
          {...register('password')}
          error={errors.password?.message}
          autoFocus
        />
        <PasswordInput
          label="Confirm Password"
          placeholder="Enter Confirm Password"
          labelClassName="!text-navy-900"
          iconClassName="!text-navy-900"
          {...register('confirmPassword')}
          error={errors.confirmPassword?.message}
          onPaste={(e) => e.preventDefault()}
        />
      </form>
    </>
  )

  const renderSuccessMessage = () => (
    <div className="flex flex-col items-center justify-center h-full">
      <CheckCircle width={48} height={48} className="text-green-500 mb-4" />
      <p className="text-navy-900 font-bold text-[28px]">Signup Complete</p>
      <p className="text-navy-900 text-sm mt-2">You may now close this window</p>
    </div>
  )

  return (
    <ExternalScreen>
      <div className="w-full h-full flex flex-col px-6 py-10">
        {successSignup ? renderSuccessMessage() : renderSignupForm()}
      </div>
    </ExternalScreen>
  )
}

export default ExternalCreatePassword